import React from 'react';
import { useTranslation } from 'react-i18next';

// Semantic type for status categorization
export type SemanticType = 'open' | 'in_progress' | 'completed' | 'deferred' | 'other';

// Interface for individual status counts (including custom statuses)
export interface StatusCount {
  id: string | null;
  name: string;
  displayName?: string;
  color: string;
  semanticType: SemanticType;
  count: number;
}

// Interface for semantic bucket counts
export interface BucketCounts {
  open: number;
  inProgress: number;
  completed: number;
  deferred: number;
}

// Legacy interface for backward compatibility
export interface TicketStatusCounts {
  postponed: number;
  open: number;
  inProgress: number;
  completed: number;
}

// Sentinel for filtering tickets with no status (calendar page only)
export const NO_STATUS_FILTER = '__no_status__' as const;

// New interface that supports both buckets and individual statuses
export interface TicketSummaryCardsProps {
  // Legacy prop - still supported for backward compatibility
  ticketStatusCounts?: TicketStatusCounts;
  // New props for semantic-type-based display
  buckets?: BucketCounts;
  allStatuses?: StatusCount[];
  className?: string;
  /** When provided (calendar page), cards are clickable and "All" is shown. Pass null for All, status id, or NO_STATUS_FILTER for no-status. */
  onStatusClick?: (statusId: string | null) => void;
  /** Selected status filter (calendar page). null = All. */
  selectedStatusId?: string | null;
}

// Helper to convert hex color to Tailwind-compatible background and text classes
function getColorClasses(color: string, semanticType: SemanticType): { bg: string; text: string; border: string } {
  // For semantic types, use predefined Tailwind classes
  switch (semanticType) {
    case 'open':
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
    case 'in_progress':
      return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
    case 'completed':
      return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' };
    case 'deferred':
      return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
    case 'other':
    default:
      // For custom statuses, use inline styles with the actual color
      return { bg: '', text: '', border: '' };
  }
}

// Check if string is a valid hex color (e.g. #abc, #aabbcc, aabbcc)
function isValidHexColor(color: string): boolean {
  if (!color || typeof color !== 'string') return false;
  const hex = color.replace('#', '').trim();
  return /^[0-9A-Fa-f]{6}$/.test(hex) || /^[0-9A-Fa-f]{3}$/.test(hex);
}

// Helper to generate inline styles from status color (from DB)
function getCustomColorStyles(color: string): React.CSSProperties {
  const hex = color.replace('#', '').trim();
  if (hex.length !== 6 && hex.length !== 3) return {};
  const r = hex.length === 6
    ? parseInt(hex.substring(0, 2), 16)
    : parseInt(hex[0] + hex[0], 16);
  const g = hex.length === 6
    ? parseInt(hex.substring(2, 4), 16)
    : parseInt(hex[1] + hex[1], 16);
  const b = hex.length === 6
    ? parseInt(hex.substring(4, 6), 16)
    : parseInt(hex[2] + hex[2], 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return {};
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.90)`,
    color: '#333333',
    borderColor: `rgba(${r}, ${g}, ${b}, 0.65)`
  };
}

function getStatusCardClassName(semanticType: SemanticType): string {
  switch (semanticType) {
    case 'deferred':
      return 'bg-gray-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-gray-200';
    case 'open':
      return 'bg-red-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-red-200';
    case 'in_progress':
      return 'bg-blue-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-blue-200';
    case 'completed':
      return 'bg-green-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-green-200';
    case 'other':
    default:
      return 'px-4 py-1.5 rounded-full text-sm font-medium border';
  }
}

export function TicketSummaryCards({ ticketStatusCounts, buckets, allStatuses, className, onStatusClick, selectedStatusId }: TicketSummaryCardsProps) {
  const { t } = useTranslation(['pages.projectDetails']);

  // If using legacy ticketStatusCounts prop, convert to new format
  const effectiveBuckets: BucketCounts = buckets || (ticketStatusCounts ? {
    open: ticketStatusCounts.open,
    inProgress: ticketStatusCounts.inProgress,
    completed: ticketStatusCounts.completed,
    deferred: ticketStatusCounts.postponed
  } : { open: 0, inProgress: 0, completed: 0, deferred: 0 });

  // Get custom statuses (semantic_type = 'other')
  const customStatuses = allStatuses?.filter(s => s.semanticType === 'other') || [];

  const isInteractive = typeof onStatusClick === 'function';

  const content = (
    <div className={`flex items-center gap-3 ${isInteractive ? 'flex-nowrap w-max' : 'flex-wrap'} ${className || ''}`}>
      {isInteractive && (
        <button
          type="button"
          onClick={() => onStatusClick(null)}
          className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border bg-white text-black hover:bg-slate-50 ${selectedStatusId === null ? 'border-black border-2' : 'border-slate-300'}`}
        >
          {t('statistics.ticketStatus.all')}
        </button>
      )}

      {isInteractive ? (
        <>
          {allStatuses?.map((status, index) => {
            const statusKey = status.id ?? NO_STATUS_FILTER;
            const isSelected = selectedStatusId === statusKey || (status.id == null && selectedStatusId === NO_STATUS_FILTER);
            // Use each status's DB color when available; fallback to semantic Tailwind only when color is missing/invalid
            const useStatusColor = isValidHexColor(status.color);
            return (
              <button
                key={status.id ?? `no-status-${index}`}
                type="button"
                onClick={() => onStatusClick(status.id ?? NO_STATUS_FILTER)}
                className={`shrink-0 ${useStatusColor ? 'px-4 py-1.5 rounded-full text-sm font-medium border' : getStatusCardClassName(status.semanticType)} ${isSelected ? '!border-black border-2' : ''}`.trim()}
                style={useStatusColor ? getCustomColorStyles(status.color) : undefined}
                title={status.displayName || status.name || t('statistics.ticketStatus.other')}
              >
                {status.displayName || status.name || t('statistics.ticketStatus.other')} {status.count}
              </button>
            );
          })}
        </>
      ) : (
        <>
          {/* Fixed semantic buckets - always shown (text black on both calendar and project pages) */}
          <div className="bg-gray-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-gray-200">
            {t('statistics.ticketStatus.postponed')} {effectiveBuckets.deferred}
          </div>
          <div className="bg-red-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-red-200">
            {t('statistics.ticketStatus.open')} {effectiveBuckets.open}
          </div>
          <div className="bg-blue-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-blue-200">
            {t('statistics.ticketStatus.inProgress')} {effectiveBuckets.inProgress}
          </div>
          <div className="bg-green-100 text-black px-4 py-1.5 rounded-full text-sm font-medium border border-green-200">
            {t('statistics.ticketStatus.completed')} {effectiveBuckets.completed}
          </div>
          {/* Dynamic custom statuses (semantic_type = 'other'); no-status records shown as "Other" */}
          {customStatuses.map((status, index) => (
            <div
              key={status.id ?? `no-status-${index}`}
              className="px-4 py-1.5 rounded-full text-sm font-medium border"
              style={getCustomColorStyles(status.color)}
            >
              {status.displayName || status.name || t('statistics.ticketStatus.other')} {status.count}
            </div>
          ))}
        </>
      )}
    </div>
  );

  if (isInteractive) {
    return (
      <div className="overflow-x-auto min-w-0 w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
        {content}
      </div>
    );
  }
  return content;
}
